import React, { useState, useMemo } from 'react'
import {
    Theme,
    makeStyles,
    TextField,
    Menu,
    MenuItem,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
} from '@material-ui/core'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import TableChartIcon from '@material-ui/icons/TableChart'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import SearchIcon from '@material-ui/icons/Search'
import SortIcon from '@material-ui/icons/Sort'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import VisibilityIcon from '@material-ui/icons/Visibility'
import DeleteIcon from '@material-ui/icons/Delete'
import CallMergeIcon from '@material-ui/icons/CallMerge'
import {
    ResidentTermDirectoryRow,
    DeliverableValue,
    MergeRequest,
} from '../../../../../models/DocumentAudit/types'
import stringSimilarity from 'string-similarity'

type SortField = 'customer' | 'unit' | 'leaseStart' | 'leaseEnd'
type SortOrder = 'asc' | 'desc'
type LeaseTerm = 'Current' | 'Future' | 'Past'

interface SortConfig {
    field: SortField
    order: SortOrder
}

interface PendingMerge {
    excelRow: ResidentTermDirectoryRow
    pdfRow: ResidentTermDirectoryRow
}

interface MergeModeProps {
    theme: Theme
    excelOnlyRows: ResidentTermDirectoryRow[]
    pdfOnlyRows: ResidentTermDirectoryRow[]
    selectedExcelRow: ResidentTermDirectoryRow | null
    selectedPdfRow: ResidentTermDirectoryRow | null
    onRowSelect: (
        row: ResidentTermDirectoryRow | null,
        type: 'excel' | 'pdf',
    ) => void
    onViewDetails: (row: ResidentTermDirectoryRow) => void
    columnKey: number
    mergeResidentTermDirectories: (params: MergeRequest) => Promise<void>
    isMerging: boolean
    leaseAuditId: number
}

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        padding: '16px',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    selectedCard: {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.light,
    },
    statusBadge: {
        display: 'flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 500,
    },
    statusIcon: {
        fontSize: '16px',
        marginRight: '4px',
    },
    detailButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    mergeButton: {
        position: 'fixed',
        bottom: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '8px 24px',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '1rem',
        fontWeight: 500,
        boxShadow: theme.shadows[2],
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: theme.shadows[4],
        },
        '&:disabled': {
            backgroundColor: theme.palette.action.disabled,
            cursor: 'not-allowed',
        },
    },
    searchField: {
        marginBottom: '16px',
        '& .MuiInputBase-root': {
            backgroundColor: theme.palette.background.paper,
        },
    },
    columnContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    header: {
        padding: '0 8px',
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    cardsContainer: {
        flex: 1,
        overflowY: 'auto',
        padding: '8px',
    },
    hideButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    showAllButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 12px',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        backgroundColor: 'transparent',
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        fontSize: '0.875rem',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    pendingMergesContainer: {
        position: 'fixed',
        bottom: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: theme.palette.background.paper,
        borderRadius: '8px',
        boxShadow: theme.shadows[4],
        padding: '16px',
        maxWidth: '800px',
        width: '90%',
        maxHeight: '300px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        zIndex: theme.zIndex.snackbar,
    },
    pendingMergesList: {
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    pendingMergeItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '8px',
        backgroundColor: theme.palette.action.hover,
        borderRadius: '4px',
    },
    pendingMergeActions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(1),
        borderTop: `1px solid ${theme.palette.divider}`,
        paddingTop: '16px',
    },
    confirmButton: {
        padding: '8px 24px',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '1rem',
        fontWeight: 500,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
        '&:disabled': {
            backgroundColor: theme.palette.action.disabled,
            cursor: 'not-allowed',
        },
    },
    clearButton: {
        padding: '8px 24px',
        backgroundColor: 'transparent',
        color: theme.palette.error.main,
        border: `1px solid ${theme.palette.error.main}`,
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '1rem',
        fontWeight: 500,
        '&:hover': {
            backgroundColor: theme.palette.error.light,
        },
    },
    dragHandle: {
        cursor: 'grab',
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.text.primary,
        },
    },
    dropTarget: {
        border: `2px dashed ${theme.palette.divider}`,
        borderRadius: '4px',
        backgroundColor: theme.palette.action.hover,
        padding: '16px',
        textAlign: 'center',
        color: theme.palette.text.secondary,
        transition: 'all 0.2s',
        '&.canDrop': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.light,
        },
    },
    pendingMerge: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
    },
    pendingMergeHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: theme.spacing(2),
    },
    pendingMergeTitle: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    pendingMergeCustomer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    pendingMergeName: {
        fontWeight: 500,
        fontSize: '0.875rem',
    },
    pendingMergeTerms: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    removeButton: {
        color: theme.palette.error.main,
    },
    tabPanel: {
        height: '100%',
        overflowY: 'auto',
        padding: '8px',
    },
    tab: {
        textTransform: 'none',
        minWidth: 'auto',
        padding: '0 16px',
    },
    tabIndicator: {
        backgroundColor: theme.palette.primary.main,
    },
}))

export const MergeMode: React.FC<MergeModeProps> = ({
    theme,
    excelOnlyRows,
    pdfOnlyRows,
    selectedExcelRow,
    selectedPdfRow,
    onRowSelect,
    onViewDetails,
    columnKey,
    mergeResidentTermDirectories,
    isMerging,
    leaseAuditId,
}) => {
    const classes = useStyles()
    const [excelSearch, setExcelSearch] = useState('')
    const [pdfSearch, setPdfSearch] = useState('')
    const [excelSort, setExcelSort] = useState<SortConfig>({
        field: 'customer',
        order: 'asc',
    })
    const [pdfSort, setPdfSort] = useState<SortConfig>({
        field: 'customer',
        order: 'asc',
    })

    // Sort menu state
    const [excelSortAnchor, setExcelSortAnchor] = useState<null | HTMLElement>(
        null,
    )
    const [pdfSortAnchor, setPdfSortAnchor] = useState<null | HTMLElement>(null)

    // Sort options
    const sortOptions = [
        { label: 'Customer Name (A-Z)', field: 'customer', order: 'asc' },
        { label: 'Customer Name (Z-A)', field: 'customer', order: 'desc' },
        { label: 'Unit (A-Z)', field: 'unit', order: 'asc' },
        { label: 'Unit (Z-A)', field: 'unit', order: 'desc' },
        { label: 'Lease Start (Earliest)', field: 'leaseStart', order: 'asc' },
        { label: 'Lease Start (Latest)', field: 'leaseStart', order: 'desc' },
        { label: 'Lease End (Earliest)', field: 'leaseEnd', order: 'asc' },
        { label: 'Lease End (Latest)', field: 'leaseEnd', order: 'desc' },
    ] as const

    // Add state for hidden rows
    const [hiddenExcelRows, setHiddenExcelRows] = useState<Set<number>>(
        new Set(),
    )
    const [hiddenPdfRows, setHiddenPdfRows] = useState<Set<number>>(new Set())

    // Add state for pending merges
    const [pendingMerges, setPendingMerges] = useState<PendingMerge[]>([])

    // Add state for selected source row
    const [selectedSourceRow, setSelectedSourceRow] = useState<{
        row: ResidentTermDirectoryRow
        type: 'excel' | 'pdf'
    } | null>(null)

    // Add this state after other state declarations
    const [selectedTerm, setSelectedTerm] = useState<LeaseTerm>('Current')

    // Helper function to format dates
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    }

    // Helper function to format value based on data type
    const formatValue = (value: DeliverableValue | undefined) => {
        if (!value) return '—'

        switch (value.entity_classification.data_type) {
            case 'DATE':
                return formatDate(value.display_value)
            case 'CURRENCY':
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                }).format(Number(value.display_value))
            case 'NUMBER':
                return new Intl.NumberFormat('en-US').format(
                    Number(value.display_value),
                )
            case 'BOOLEAN':
                return value.display_value.toLowerCase() === 'true'
                    ? 'Yes'
                    : 'No'
            default:
                return value.display_value
        }
    }

    // Helper function to toggle row visibility
    const toggleRowVisibility = (rowId: number, type: 'excel' | 'pdf') => {
        if (type === 'excel') {
            setHiddenExcelRows((prev) => {
                const newSet = new Set(prev)
                if (prev.has(rowId)) {
                    newSet.delete(rowId)
                } else {
                    newSet.add(rowId)
                }
                return newSet
            })
        } else {
            setHiddenPdfRows((prev) => {
                const newSet = new Set(prev)
                if (prev.has(rowId)) {
                    newSet.delete(rowId)
                } else {
                    newSet.add(rowId)
                }
                return newSet
            })
        }
    }

    // Show all hidden rows
    const showAllRows = (type: 'excel' | 'pdf') => {
        if (type === 'excel') {
            setHiddenExcelRows(new Set())
        } else {
            setHiddenPdfRows(new Set())
        }
    }

    // Filter rows based on search term
    const filterRows = (
        rows: ResidentTermDirectoryRow[],
        searchTerm: string,
    ) => {
        if (!searchTerm) return rows

        const searchLower = searchTerm.toLowerCase()
        return rows.filter((row) => {
            const searchableFields = [
                row.customer?.name ?? '',
                row.bldg_unit ?? '',
                row.lease_start ? formatDate(row.lease_start) : '',
                row.lease_end ? formatDate(row.lease_end) : '',
            ]
            return searchableFields.some((field) =>
                field.toLowerCase().includes(searchLower),
            )
        })
    }

    // Sort function
    const sortRows = (
        rows: ResidentTermDirectoryRow[],
        sortConfig: SortConfig,
    ) => {
        return [...rows].sort((a, b) => {
            let aValue: string | number | Date
            let bValue: string | number | Date

            switch (sortConfig.field) {
                case 'customer':
                    aValue = a.customer?.name ?? ''
                    bValue = b.customer?.name ?? ''
                    break
                case 'unit':
                    aValue = a.bldg_unit ?? ''
                    bValue = b.bldg_unit ?? ''
                    break
                case 'leaseStart':
                    aValue = new Date(a.lease_start)
                    bValue = new Date(b.lease_start)
                    break
                case 'leaseEnd':
                    aValue = new Date(a.lease_end)
                    bValue = new Date(b.lease_end)
                    break
                default:
                    return 0
            }

            if (sortConfig.order === 'asc') {
                return aValue > bValue ? 1 : -1
            } else {
                return aValue < bValue ? 1 : -1
            }
        })
    }

    // Helper function to check if a row is in pending merges
    const isRowInPendingMerges = (
        row: ResidentTermDirectoryRow,
        type: 'excel' | 'pdf',
    ) => {
        return pendingMerges.some((merge) =>
            type === 'excel'
                ? merge.excelRow.id === row.id
                : merge.pdfRow.id === row.id,
        )
    }

    // Add function to calculate similarity between customer names
    const calculateSimilarity = (name1: string, name2: string): number => {
        const n1 = name1.toLowerCase()
        const n2 = name2.toLowerCase()

        // Exact match (case-insensitive)
        if (n1 === n2) return 1

        // Starts with match
        if (n1.startsWith(n2) || n2.startsWith(n1)) return 0.8

        // Contains match
        if (n1.includes(n2) || n2.includes(n1)) return 0.6

        // Calculate string similarity for other cases
        return stringSimilarity.compareTwoStrings(n1, n2)
    }

    // Add function to get similarity color based on score
    const getSimilarityColor = (score: number): string => {
        if (score >= 0.8) return theme.palette.success.main
        if (score >= 0.6) return theme.palette.info.main
        if (score >= 0.4) return theme.palette.warning.main
        return theme.palette.error.main
    }

    // Add function to sort rows by similarity to a target name
    const sortBySimilarity = (
        rows: ResidentTermDirectoryRow[],
        targetName: string,
    ) => {
        return [...rows].sort((a, b) => {
            const similarityA = calculateSimilarity(a.customer.name, targetName)
            const similarityB = calculateSimilarity(b.customer.name, targetName)

            // If similarity is the same, sort alphabetically
            if (Math.abs(similarityA - similarityB) < 0.1) {
                return a.customer.name.localeCompare(b.customer.name)
            }

            return similarityB - similarityA // Sort in descending order (most similar first)
        })
    }

    // Add this helper function after the other helper functions
    const getLeaseTerm = (
        row: ResidentTermDirectoryRow,
    ): 'Current' | 'Future' | 'Past' => {
        const today = new Date()
        const startDate = new Date(row.lease_start)
        const endDate = new Date(row.lease_end)

        if (startDate <= today && endDate >= today) {
            return 'Current'
        } else if (startDate > today) {
            return 'Future'
        } else {
            return 'Past'
        }
    }

    // Add this helper function to group rows by lease term
    const groupRowsByTerm = (rows: ResidentTermDirectoryRow[]) => {
        return {
            Current: rows.filter((row) => getLeaseTerm(row) === 'Current'),
            Future: rows.filter((row) => getLeaseTerm(row) === 'Future'),
            Past: rows.filter((row) => getLeaseTerm(row) === 'Past'),
        }
    }

    // Update the filtered rows to use the grouping
    const filteredExcelRowsByTerm = useMemo(() => {
        let filtered = excelOnlyRows.filter(
            (row) => !hiddenExcelRows.has(row.id),
        )
        if (excelSearch) {
            filtered = filtered.filter((row) =>
                row.customer.name
                    .toLowerCase()
                    .includes(excelSearch.toLowerCase()),
            )
        }

        // If a PDF row is selected, sort by similarity to its customer name
        if (selectedPdfRow) {
            filtered = sortBySimilarity(filtered, selectedPdfRow.customer.name)
            // Only show rows with similarity above threshold and matching lease term
            filtered = filtered.filter(
                (row) =>
                    calculateSimilarity(
                        row.customer.name,
                        selectedPdfRow.customer.name,
                    ) >= 0.3 &&
                    getLeaseTerm(row) === getLeaseTerm(selectedPdfRow),
            )
        } else if (excelSort.field) {
            // Otherwise use the normal sorting
            filtered = sortRows(filtered, excelSort)
        }

        return groupRowsByTerm(filtered)
    }, [excelOnlyRows, hiddenExcelRows, excelSearch, excelSort, selectedPdfRow])

    const filteredPdfRowsByTerm = useMemo(() => {
        let filtered = pdfOnlyRows.filter((row) => !hiddenPdfRows.has(row.id))
        if (pdfSearch) {
            filtered = filtered.filter((row) =>
                row.customer.name
                    .toLowerCase()
                    .includes(pdfSearch.toLowerCase()),
            )
        }

        // If an Excel row is selected, sort by similarity to its customer name
        if (selectedExcelRow) {
            filtered = sortBySimilarity(
                filtered,
                selectedExcelRow.customer.name,
            )
            // Only show rows with similarity above threshold and matching lease term
            filtered = filtered.filter(
                (row) =>
                    calculateSimilarity(
                        row.customer.name,
                        selectedExcelRow.customer.name,
                    ) >= 0.3 &&
                    getLeaseTerm(row) === getLeaseTerm(selectedExcelRow),
            )
        } else if (pdfSort.field) {
            // Otherwise use the normal sorting
            filtered = sortRows(filtered, pdfSort)
        }

        return groupRowsByTerm(filtered)
    }, [pdfOnlyRows, hiddenPdfRows, pdfSearch, pdfSort, selectedExcelRow])

    // Get display text for current sort
    const getSortDisplayText = (sortConfig: SortConfig) => {
        switch (sortConfig.field) {
            case 'customer':
                return 'Name'
            case 'unit':
                return 'Unit'
            case 'leaseStart':
                return 'Start'
            case 'leaseEnd':
                return 'End'
            default:
                return 'Sort'
        }
    }

    // Sort button style
    const sortButtonStyle = {
        display: 'flex',
        alignItems: 'center',
        padding: '4px 8px',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        color: theme.palette.text.primary,
        fontSize: '0.875rem',
        gap: '4px',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    }

    // Handle adding a new merge
    const handleAddMerge = (
        excelRow: ResidentTermDirectoryRow,
        pdfRow: ResidentTermDirectoryRow,
    ) => {
        setPendingMerges((prev) => [...prev, { excelRow, pdfRow }])
    }

    // Handle removing a merge
    const handleRemoveMerge = (index: number) => {
        setPendingMerges((prev) => prev.filter((_, i) => i !== index))
    }

    // Handle confirming all merges
    const handleConfirmMerges = async () => {
        try {
            await mergeResidentTermDirectories({
                lease_audit_id: leaseAuditId,
                merges: pendingMerges.map((merge) => ({
                    excel_row_id: merge.excelRow.id,
                    pdf_row_id: merge.pdfRow.id,
                })),
            })
            setPendingMerges([])
        } catch (error) {
            console.error('Error merging directories:', error)
        }
    }

    // Handle clearing all pending merges
    const handleClearMerges = () => {
        setPendingMerges([])
    }

    // Add this handler after other handlers
    const handleTermChange = (
        event: React.ChangeEvent<{}>,
        newValue: LeaseTerm,
    ) => {
        setSelectedTerm(newValue)
    }

    // Updated render row card function
    const renderRowCard = (
        row: ResidentTermDirectoryRow,
        type: 'excel' | 'pdf',
    ) => {
        const isExcel = type === 'excel'
        const Icon = isExcel ? TableChartIcon : PictureAsPdfIcon
        const iconColor = isExcel
            ? theme.palette.warning.dark
            : theme.palette.info.dark
        const bgColor = isExcel
            ? theme.palette.warning.light
            : theme.palette.info.light

        const leaseTerm = getLeaseTerm(row)
        const termColor = {
            Current: theme.palette.success.main,
            Future: theme.palette.info.main,
            Past: theme.palette.text.secondary,
        }[leaseTerm]

        const selectedValue = columnKey
            ? row.values.find((v) => v.entity_classification.id === columnKey)
            : undefined

        // Calculate similarity score if there's a selected row in the opposite column
        const similarityScore =
            isExcel && selectedPdfRow
                ? calculateSimilarity(
                      row.customer.name,
                      selectedPdfRow.customer.name,
                  )
                : !isExcel && selectedExcelRow
                ? calculateSimilarity(
                      row.customer.name,
                      selectedExcelRow.customer.name,
                  )
                : null

        const handleDetailClick = (e: React.MouseEvent) => {
            e.stopPropagation()
            onViewDetails(row)
        }

        const handleHideClick = (e: React.MouseEvent) => {
            e.stopPropagation()
            toggleRowVisibility(row.id, type)
        }

        const handleCardClick = (e: React.MouseEvent) => {
            e.stopPropagation()

            // If clicking the same row, deselect it
            if (
                (isExcel && selectedExcelRow?.id === row.id) ||
                (!isExcel && selectedPdfRow?.id === row.id)
            ) {
                onRowSelect(null, type)
            } else {
                // Otherwise, select the row
                onRowSelect(row, type)
            }
        }

        const handleMergeClick = (e: React.MouseEvent) => {
            e.stopPropagation()

            // If we have a selected row from the opposite column, create a merge
            if (isExcel && selectedPdfRow) {
                if (getLeaseTerm(row) === getLeaseTerm(selectedPdfRow)) {
                    handleAddMerge(row, selectedPdfRow)
                    onRowSelect(null, 'pdf') // Clear the PDF selection
                } else {
                    // Show error message or disable merge button
                    console.log('Cannot merge rows with different lease terms')
                }
            } else if (!isExcel && selectedExcelRow) {
                if (getLeaseTerm(row) === getLeaseTerm(selectedExcelRow)) {
                    handleAddMerge(selectedExcelRow, row)
                    onRowSelect(null, 'excel') // Clear the Excel selection
                } else {
                    // Show error message or disable merge button
                    console.log('Cannot merge rows with different lease terms')
                }
            }
        }

        const isSelected = isExcel
            ? selectedExcelRow?.id === row.id
            : selectedPdfRow?.id === row.id

        const canMerge =
            (isExcel && selectedPdfRow) || (!isExcel && selectedExcelRow)

        return (
            <div
                className={`${classes.card} ${
                    isSelected ? classes.selectedCard : ''
                }`}
                style={{
                    position: 'relative',
                    border: `1px solid ${
                        isSelected
                            ? theme.palette.primary.main
                            : theme.palette.divider
                    }`,
                    cursor: 'pointer',
                }}
                onClick={handleCardClick}
            >
                <div
                    style={{
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <div
                            style={{
                                padding: '2px 6px',
                                borderRadius: '4px',
                                backgroundColor: termColor,
                                color: theme.palette.common.white,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                            }}
                        >
                            {leaseTerm}
                        </div>
                        <button
                            className={classes.detailButton}
                            onClick={handleDetailClick}
                            title="View Details"
                        >
                            <InfoOutlinedIcon
                                style={{
                                    fontSize: '20px',
                                    width: '20px',
                                    height: '20px',
                                }}
                            />
                        </button>
                        <button
                            className={classes.hideButton}
                            onClick={handleHideClick}
                            title="Hide Row"
                        >
                            <VisibilityOffIcon
                                style={{
                                    fontSize: '20px',
                                    width: '20px',
                                    height: '20px',
                                    color: theme.palette.text.secondary,
                                }}
                            />
                        </button>
                        <div
                            style={{
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {row.customer.name}
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        {similarityScore !== null && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (canMerge) {
                                        handleMergeClick(e)
                                    }
                                }}
                                style={{
                                    position: 'relative',
                                    padding: '12px',
                                    cursor: canMerge ? 'pointer' : 'default',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s ease-in-out',
                                    backgroundColor: 'transparent',
                                }}
                                onMouseEnter={(e) => {
                                    if (canMerge) {
                                        e.currentTarget.style.backgroundColor =
                                            theme.palette.action.hover
                                        e.currentTarget.style.boxShadow = `0 0 0 2px ${theme.palette.primary.main}`
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (canMerge) {
                                        e.currentTarget.style.backgroundColor =
                                            'transparent'
                                        e.currentTarget.style.boxShadow = 'none'
                                    }
                                }}
                            >
                                <Tooltip
                                    title={
                                        canMerge
                                            ? 'Click to merge'
                                            : `${Math.round(
                                                  similarityScore * 100,
                                              )}% similar`
                                    }
                                >
                                    <div
                                        style={{
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: getSimilarityColor(
                                                similarityScore,
                                            ),
                                            color: theme.palette.common.white,
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            transition: 'all 0.2s ease-in-out',
                                            opacity: 1,
                                            boxShadow: canMerge
                                                ? theme.shadows[1]
                                                : 'none',
                                            transform: 'translateY(0)',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (canMerge) {
                                                e.currentTarget.style.opacity =
                                                    '0.9'
                                                e.currentTarget.style.transform =
                                                    'translateY(-1px)'
                                                e.currentTarget.style.boxShadow =
                                                    theme.shadows[2]
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (canMerge) {
                                                e.currentTarget.style.opacity =
                                                    '1'
                                                e.currentTarget.style.transform =
                                                    'translateY(0)'
                                                e.currentTarget.style.boxShadow =
                                                    theme.shadows[1]
                                            }
                                        }}
                                    >
                                        {Math.round(similarityScore * 100)}%
                                        {canMerge && (
                                            <CallMergeIcon
                                                style={{
                                                    fontSize: '16px',
                                                    marginLeft: '2px',
                                                }}
                                            />
                                        )}
                                    </div>
                                </Tooltip>
                            </div>
                        )}
                        <div
                            className={classes.statusBadge}
                            style={{ backgroundColor: bgColor }}
                        >
                            <Icon
                                className={classes.statusIcon}
                                style={{ color: iconColor }}
                            />
                            {isExcel ? 'Excel Only' : 'PDF Only'}
                        </div>
                    </div>
                </div>

                {/* Details Grid */}
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '120px 1fr',
                        gap: '8px 16px',
                        fontSize: '0.875rem',
                    }}
                >
                    <div
                        style={{
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                        }}
                    >
                        Unit
                    </div>
                    <div
                        style={{
                            color: theme.palette.text.primary,
                            fontWeight: 400,
                        }}
                    >
                        {row.bldg_unit}
                    </div>

                    <div
                        style={{
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                        }}
                    >
                        Lease Period
                    </div>
                    <div
                        style={{
                            color: theme.palette.text.primary,
                            fontWeight: 400,
                        }}
                    >
                        {formatDate(row.lease_start)} -{' '}
                        {formatDate(row.lease_end)}
                    </div>

                    <div
                        style={{
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                        }}
                    >
                        Value
                    </div>
                    <div
                        style={{
                            color: theme.palette.text.primary,
                            fontWeight: 400,
                        }}
                    >
                        {formatValue(selectedValue)}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
            {/* Single Tabs Section */}
            <div
                style={{
                    padding: '0 16px',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                }}
            >
                <Tabs
                    value={selectedTerm}
                    onChange={handleTermChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    classes={{
                        indicator: classes.tabIndicator,
                    }}
                >
                    <Tab
                        label={`Current (${
                            filteredExcelRowsByTerm.Current.length +
                            filteredPdfRowsByTerm.Current.length
                        })`}
                        value="Current"
                        className={classes.tab}
                    />
                    <Tab
                        label={`Future (${
                            filteredExcelRowsByTerm.Future.length +
                            filteredPdfRowsByTerm.Future.length
                        })`}
                        value="Future"
                        className={classes.tab}
                    />
                    <Tab
                        label={`Past (${
                            filteredExcelRowsByTerm.Past.length +
                            filteredPdfRowsByTerm.Past.length
                        })`}
                        value="Past"
                        className={classes.tab}
                    />
                </Tabs>
            </div>

            {/* Columns Container */}
            <div
                style={{
                    display: 'flex',
                    gap: '16px',
                    flex: 1,
                    overflow: 'hidden',
                    marginTop: '16px',
                    padding: '0 16px',
                }}
            >
                {/* Excel Only Column */}
                <div className={classes.columnContainer}>
                    <div className={classes.header}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '8px',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <div
                                    style={{
                                        width: '4px',
                                        height: '20px',
                                        backgroundColor:
                                            theme.palette.warning.main,
                                        borderRadius: '2px',
                                    }}
                                />
                                <div
                                    style={{
                                        fontSize: '1.25rem',
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    Excel Only Rows
                                    <span
                                        style={{
                                            fontSize: '0.875rem',
                                            color: theme.palette.text.secondary,
                                            fontWeight: 500,
                                        }}
                                    >
                                        ({excelOnlyRows.length})
                                    </span>
                                </div>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center',
                                }}
                            >
                                {hiddenExcelRows.size > 0 && (
                                    <button
                                        className={classes.showAllButton}
                                        onClick={() => showAllRows('excel')}
                                    >
                                        <VisibilityIcon
                                            style={{ fontSize: '18px' }}
                                        />
                                        Show {hiddenExcelRows.size} Hidden
                                    </button>
                                )}
                                <button
                                    onClick={(e) =>
                                        setExcelSortAnchor(e.currentTarget)
                                    }
                                    style={sortButtonStyle}
                                >
                                    <SortIcon style={{ fontSize: '18px' }} />
                                    {getSortDisplayText(excelSort)}
                                    {excelSort.order === 'asc' ? (
                                        <ArrowUpwardIcon
                                            style={{ fontSize: '18px' }}
                                        />
                                    ) : (
                                        <ArrowDownwardIcon
                                            style={{ fontSize: '18px' }}
                                        />
                                    )}
                                </button>
                            </div>
                        </div>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search by customer, unit, or lease period..."
                            value={excelSearch}
                            onChange={(e) => setExcelSearch(e.target.value)}
                            className={classes.searchField}
                            InputProps={{
                                startAdornment: (
                                    <SearchIcon
                                        style={{
                                            marginRight: '8px',
                                            color: theme.palette.text.secondary,
                                        }}
                                    />
                                ),
                            }}
                        />
                    </div>
                    <div className={classes.tabPanel}>
                        {filteredExcelRowsByTerm[selectedTerm].map((row) => (
                            <div key={row.id}>
                                {renderRowCard(row, 'excel')}
                            </div>
                        ))}
                    </div>
                    <Menu
                        anchorEl={excelSortAnchor}
                        open={Boolean(excelSortAnchor)}
                        onClose={() => setExcelSortAnchor(null)}
                    >
                        {sortOptions.map((option) => (
                            <MenuItem
                                key={`${option.field}-${option.order}`}
                                onClick={() => {
                                    setExcelSort({
                                        field: option.field as SortField,
                                        order: option.order as SortOrder,
                                    })
                                    setExcelSortAnchor(null)
                                }}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </div>

                {/* PDF Only Column */}
                <div className={classes.columnContainer}>
                    <div className={classes.header}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '8px',
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                <div
                                    style={{
                                        width: '4px',
                                        height: '20px',
                                        backgroundColor:
                                            theme.palette.info.main,
                                        borderRadius: '2px',
                                    }}
                                />
                                <div
                                    style={{
                                        fontSize: '1.25rem',
                                        fontWeight: 600,
                                        color: theme.palette.text.primary,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    PDF Only Rows
                                    <span
                                        style={{
                                            fontSize: '0.875rem',
                                            color: theme.palette.text.secondary,
                                            fontWeight: 500,
                                        }}
                                    >
                                        ({pdfOnlyRows.length})
                                    </span>
                                </div>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center',
                                }}
                            >
                                {hiddenPdfRows.size > 0 && (
                                    <button
                                        className={classes.showAllButton}
                                        onClick={() => showAllRows('pdf')}
                                    >
                                        <VisibilityIcon
                                            style={{ fontSize: '18px' }}
                                        />
                                        Show {hiddenPdfRows.size} Hidden
                                    </button>
                                )}
                                <button
                                    onClick={(e) =>
                                        setPdfSortAnchor(e.currentTarget)
                                    }
                                    style={sortButtonStyle}
                                >
                                    <SortIcon style={{ fontSize: '18px' }} />
                                    {getSortDisplayText(pdfSort)}
                                    {pdfSort.order === 'asc' ? (
                                        <ArrowUpwardIcon
                                            style={{ fontSize: '18px' }}
                                        />
                                    ) : (
                                        <ArrowDownwardIcon
                                            style={{ fontSize: '18px' }}
                                        />
                                    )}
                                </button>
                            </div>
                        </div>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search by customer, unit, or lease period..."
                            value={pdfSearch}
                            onChange={(e) => setPdfSearch(e.target.value)}
                            className={classes.searchField}
                            InputProps={{
                                startAdornment: (
                                    <SearchIcon
                                        style={{
                                            marginRight: '8px',
                                            color: theme.palette.text.secondary,
                                        }}
                                    />
                                ),
                            }}
                        />
                    </div>
                    <div className={classes.tabPanel}>
                        {filteredPdfRowsByTerm[selectedTerm].map((row) => (
                            <div key={row.id}>{renderRowCard(row, 'pdf')}</div>
                        ))}
                    </div>
                    <Menu
                        anchorEl={pdfSortAnchor}
                        open={Boolean(pdfSortAnchor)}
                        onClose={() => setPdfSortAnchor(null)}
                    >
                        {sortOptions.map((option) => (
                            <MenuItem
                                key={`${option.field}-${option.order}`}
                                onClick={() => {
                                    setPdfSort({
                                        field: option.field as SortField,
                                        order: option.order as SortOrder,
                                    })
                                    setPdfSortAnchor(null)
                                }}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </div>
            </div>

            {/* Pending Merges Container */}
            {pendingMerges.length > 0 && (
                <div className={classes.pendingMergesContainer}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <h3 style={{ margin: 0 }}>
                            Pending Merges ({pendingMerges.length})
                        </h3>
                    </div>
                    <div className={classes.pendingMergesList}>
                        {pendingMerges.map((merge, index) => (
                            <div
                                key={index}
                                className={classes.pendingMergeItem}
                            >
                                <div style={{ flex: 1 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '4px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            <TableChartIcon
                                                style={{
                                                    color:
                                                        theme.palette.warning
                                                            .dark,
                                                }}
                                            />
                                            {merge.excelRow.customer.name}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '0.75rem',
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                                marginLeft: '32px',
                                            }}
                                        >
                                            {merge.excelRow.lease_start &&
                                                formatDate(
                                                    merge.excelRow.lease_start,
                                                )}{' '}
                                            -{' '}
                                            {merge.excelRow.lease_end &&
                                                formatDate(
                                                    merge.excelRow.lease_end,
                                                )}
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '4px',
                                            marginTop: '8px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                            }}
                                        >
                                            <PictureAsPdfIcon
                                                style={{
                                                    color:
                                                        theme.palette.info.dark,
                                                }}
                                            />
                                            {merge.pdfRow.customer.name}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: '0.75rem',
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                                marginLeft: '32px',
                                            }}
                                        >
                                            {merge.pdfRow.lease_start &&
                                                formatDate(
                                                    merge.pdfRow.lease_start,
                                                )}{' '}
                                            -{' '}
                                            {merge.pdfRow.lease_end &&
                                                formatDate(
                                                    merge.pdfRow.lease_end,
                                                )}
                                        </div>
                                    </div>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    <Tooltip
                                        title={`${Math.round(
                                            calculateSimilarity(
                                                merge.excelRow.customer.name,
                                                merge.pdfRow.customer.name,
                                            ) * 100,
                                        )}% similar`}
                                    >
                                        <div
                                            style={{
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: getSimilarityColor(
                                                    calculateSimilarity(
                                                        merge.excelRow.customer
                                                            .name,
                                                        merge.pdfRow.customer
                                                            .name,
                                                    ),
                                                ),
                                                color:
                                                    theme.palette.common.white,
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {Math.round(
                                                calculateSimilarity(
                                                    merge.excelRow.customer
                                                        .name,
                                                    merge.pdfRow.customer.name,
                                                ) * 100,
                                            )}
                                            %
                                        </div>
                                    </Tooltip>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveMerge(index)}
                                        className={classes.removeButton}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={classes.pendingMergeActions}>
                        <button
                            className={classes.clearButton}
                            onClick={handleClearMerges}
                        >
                            Clear All
                        </button>
                        <button
                            className={classes.confirmButton}
                            onClick={handleConfirmMerges}
                            disabled={isMerging}
                        >
                            {isMerging ? 'Merging...' : 'Confirm Merges'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
